import parse from 'date-fns/parse'
import { OpeningHourItem, OpeningHours } from '@instabox/consumer-web-shared'

import getTranslation from './getTranslation'
import getWeekdaysForLocale from './getWeekdaysForLocale'

export type FormattedOpeningHour = OpeningHourItem & {
  days: string
  hours: string
}

const formatWeekdayIndexes = (days: string[], weekdays: string[], separator: string) => {
  return days
    .map((day: string) => weekdays[day])
    .filter((_, index: number) => index === 0 || index === days.length - 1)
    .join(separator)
}

interface FormatOpeningHours {
  openingHours: OpeningHours
  separator?: string
  locales: string | string[]
}

interface FormatOpenCloseRange extends FormatOpeningHours {
  daysFormatter: (openingHour: OpeningHourItem) => string
}

const formatOpenCloseRange = ({
  openingHours,
  separator = '–',
  locales,
  daysFormatter,
}: FormatOpenCloseRange): FormattedOpeningHour[] => {
  const timeFormatter = new Intl.DateTimeFormat(locales, {
    hour: 'numeric',
    minute: 'numeric',
  })
  const timeFormat = 'HH:mm:ss'
  return openingHours.map(openingHour => {
    const days = daysFormatter(openingHour)
    if (openingHour.is_closed) return { ...openingHour, days, hours: '' }
    const openTime = parse(openingHour.open, timeFormat, new Date())
    const closeTime = parse(openingHour.close, timeFormat, new Date())
    const hours = timeFormatter.format(openTime) + separator + timeFormatter.format(closeTime)
    return { ...openingHour, days, hours }
  })
}

export const formatOpeningHours = ({
  openingHours,
  separator = '–',
  locales,
}: FormatOpeningHours): FormattedOpeningHour[] => {
  const weekdays = getWeekdaysForLocale({
    locales,
    options: {
      weekday: 'short',
    },
  })
  return formatOpenCloseRange({
    openingHours,
    separator,
    locales,
    daysFormatter: openingHour => {
      if (openingHour.__typename === 'opening_hours') {
        return formatWeekdayIndexes(openingHour.days, weekdays, separator)
      }
      return ''
    },
  })
}

export const formatSpecialOpeningHours = ({
  openingHours,
  separator = '–',
  locales,
}: FormatOpeningHours): FormattedOpeningHour[] => {
  const dateFormat = 'yyyy-LL-dd'
  const dateFormatter = new Intl.DateTimeFormat(locales, {
    dateStyle: 'long',
  })
  return formatOpenCloseRange({
    openingHours,
    separator,
    locales,
    daysFormatter: openingHour => {
      if (openingHour.__typename === 'special_opening_hours') {
        const dayString = openingHour.date
          ? dateFormatter.format(parse(openingHour.date, dateFormat, new Date()))
          : ''
        const title = getTranslation('title', openingHour.translations, openingHour.title) || ''
        return `${title} ${dayString}`
      }
      return ''
    },
  })
}

export default formatOpeningHours
